// SkipCustomerCreatePage — /skip/customers/create.
//
// Feature 1: a thin form for creating a customer scoped to the skip
// module. Posts to POST /api/v1/skip/customers, which auto-includes
// branch='skip' on the customer_branches junction so the new row
// appears in the default Skip Customers list immediately. The form
// covers the legacy minimum: name + phone + postcode (the customers
// schema requires phone OR email plus address+postcode).
//
// On success: navigate to /skip/customers/:enc_id (the detail page).

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { Button } from '../../../components/ui/Button/Button';
import { Card } from '../../../components/ui/Card/Card';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { useCreateSkipCustomer } from '../hooks/useSkipCustomers';
import { extractErrorMessage } from '../../../utils/errorMessage';
import type { CreateCustomerInput, CustomerType } from '../../../types/api';
import styles from './SkipCustomerCreatePage.module.css';

interface FormState {
  customer_type: CustomerType;
  first_name: string;
  last_name: string;
  company_name: string;
  email: string;
  phone: string;
  phone2: string;
  address: string;
  postcode: string;
}

const EMPTY: FormState = {
  customer_type: 'residential',
  first_name: '',
  last_name: '',
  company_name: '',
  email: '',
  phone: '',
  phone2: '',
  address: '',
  postcode: '',
};

export function SkipCustomerCreatePage(): JSX.Element {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const create = useCreateSkipCustomer();

  // Feature 3 mirror — the server derives `name` from first/last/
  // company anyway, but surfacing the computed value in the form
  // keeps the UX honest about what will be saved.
  const computedName =
    form.customer_type === 'commercial' && form.company_name.trim().length > 0
      ? form.company_name.trim()
      : `${form.first_name.trim()} ${form.last_name.trim()}`.trim();

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    setError(null);
    if (computedName.length < 2) {
      setError('Please enter a customer name (first/last or company).');
      return;
    }
    if (!form.phone.trim() && !form.email.trim()) {
      setError('At least one of phone or email is required.');
      return;
    }
    if (!form.address.trim() || !form.postcode.trim()) {
      setError('Address and postcode are required.');
      return;
    }
    const body: CreateCustomerInput = {
      customer_type: form.customer_type,
      name: computedName,
      first_name: form.first_name.trim() || null,
      last_name: form.last_name.trim() || null,
      company_name: form.company_name.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      phone2: form.phone2.trim() || null,
      address: form.address.trim(),
      postcode: form.postcode.trim(),
      account_status: 'active',
    };
    create.mutate(body, {
      onSuccess: (data) => {
        navigate(`/skip/customers/${encodeURIComponent(data.customer.enc_id)}`);
      },
      onError: (err) => setError(extractErrorMessage(err)),
    });
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="New Skip Customer"
        subtitle="Create a customer record and add them to the Skip branch."
        actions={
          <Button
            variant="secondary"
            icon={ArrowLeft}
            onClick={() => navigate('/skip/customers')}
          >
            Back to Customers
          </Button>
        }
      />

      <form onSubmit={handleSubmit}>
        <Card>
          <h2 className={styles.heading}>Customer</h2>
          <div className={styles.grid}>
            <label className={styles.field}>
              <span>Customer type</span>
              <select
                value={form.customer_type}
                onChange={(e) =>
                  setForm({
                    ...form,
                    customer_type: e.target.value as CustomerType,
                  })
                }
              >
                <option value="residential">Residential</option>
                <option value="commercial">Commercial</option>
              </select>
            </label>
            <label className={styles.field}>
              <span>First name</span>
              <input
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              />
            </label>
            <label className={styles.field}>
              <span>Last name</span>
              <input
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              />
            </label>
            <label className={styles.field}>
              <span>Company name</span>
              <input
                value={form.company_name}
                onChange={(e) =>
                  setForm({ ...form, company_name: e.target.value })
                }
              />
            </label>
            <label className={styles.field}>
              <span>Email</span>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </label>
            <label className={styles.field}>
              <span>Phone *</span>
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </label>
            <label className={styles.field}>
              <span>Secondary phone</span>
              <input
                value={form.phone2}
                onChange={(e) => setForm({ ...form, phone2: e.target.value })}
              />
            </label>
            <label className={styles.field}>
              <span>Postcode *</span>
              <input
                value={form.postcode}
                onChange={(e) => setForm({ ...form, postcode: e.target.value })}
              />
            </label>
          </div>
          <label className={[styles.field, styles.fieldFull].join(' ')}>
            <span>Address *</span>
            <textarea
              rows={2}
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
            />
          </label>
          {computedName.length > 0 ? (
            <p className={styles.note}>
              Saved as <strong>{computedName}</strong>.
            </p>
          ) : null}
        </Card>

        {error ? <p className={styles.error}>{error}</p> : null}

        <div className={styles.formActions}>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/skip/customers')}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create Customer'}
          </Button>
        </div>
      </form>
    </div>
  );
}
